/**
 * Cây thư mục tài liệu báo cáo, dựng từ danh sách phẳng của `GET /folders` (đã sắp theo `path`, cha
 * trước con). Chép từ `cay-thu-muc.ts` của native, bỏ `organizationId`.
 */
import type { Folder } from '../types.ts';

export interface NutCay extends Folder {
  con: NutCay[];
  /** Độ sâu, gốc là 0: thụt lề lúc vẽ mà không phải đệ quy. */
  sau: number;
}

/**
 * Cây từ danh sách phẳng, O(n): một lượt dựng `Map`, một lượt nối con vào cha.
 *
 * Nút có `parentId` trỏ vào id không có trong danh sách (dữ liệu lệch) được coi là **gốc**, không bị
 * bỏ: bỏ đi là cả nhánh biến mất khỏi màn hình mà không ai biết.
 */
export function dungCay(ds: readonly Folder[]): NutCay[] {
  const theoId = new Map<string, NutCay>();
  for (const f of ds) theoId.set(f.id, { ...f, con: [], sau: 0 });

  const goc: NutCay[] = [];
  for (const f of ds) {
    const nut = theoId.get(f.id);
    if (nut === undefined) continue;
    const cha = f.parentId === null ? undefined : theoId.get(f.parentId);
    if (cha === undefined || cha === nut) goc.push(nut);
    else cha.con.push(nut);
  }

  // Độ sâu tính sau khi nối xong; có trần để dữ liệu chu trình không làm tràn ngăn xếp.
  const datSau = (ns: NutCay[], sau: number): void => {
    if (sau > 512) return;
    for (const n of ns) {
      n.sau = sau;
      datSau(n.con, sau + 1);
    }
  };
  datSau(goc, 0);
  return goc;
}

/** Trải cây thành danh sách theo thứ tự hiển thị, bỏ qua nhánh đang đóng. */
export function traiCay(goc: readonly NutCay[], dangMo: ReadonlySet<string>): NutCay[] {
  const ra: NutCay[] = [];
  const di = (ns: readonly NutCay[], sau: number): void => {
    if (sau > 512) return;
    for (const n of ns) {
      ra.push(n);
      if (n.con.length > 0 && dangMo.has(n.id)) di(n.con, sau + 1);
    }
  };
  di(goc, 0);
  return ra;
}

/**
 * Chuỗi id từ gốc xuống tới `id`, gồm cả chính nó — để mở sẵn đường tới thư mục đang chọn. Không tìm
 * thấy thì mảng rỗng. Có trần vòng lặp: dữ liệu lỡ có chu trình thì dừng, không treo trang.
 */
export function duongToiGoc(ds: readonly Folder[], id: string): string[] {
  const theoId = new Map(ds.map((f) => [f.id, f]));
  const duong: string[] = [];
  let hienTai: string | null = id;

  for (let buoc = 0; buoc < 512; buoc += 1) {
    if (hienTai === null) return duong.reverse();
    const f: Folder | undefined = theoId.get(hienTai);
    if (f === undefined) return duong.length > 0 ? duong.reverse() : [];
    duong.push(f.id);
    hienTai = f.parentId;
  }
  return duong.reverse();
}

/** Tên đầy đủ theo cây (`Báo cáo tháng › 2026-08`). */
export function tenDayDu(ds: readonly Folder[], id: string): string {
  const theoId = new Map(ds.map((f) => [f.id, f]));
  return duongToiGoc(ds, id)
    .map((x) => theoId.get(x)?.name ?? '?')
    .join(' › ');
}

/**
 * Lựa chọn cho ô chọn thư mục: mở hết mọi nhánh (để chọn được mọi thư mục), theo thứ tự hiển thị.
 *
 * `boNhanh` loại chính thư mục đó và mọi con cháu — chuyển thư mục vào nhánh của chính nó là chu trình,
 * server trả `400 BAO_CAO_FOLDER_CYCLE`; bày ra một lựa chọn chắc chắn hỏng là bắt người dùng đoán.
 * So con cháu theo cây chứ không theo `path`, để không phụ thuộc cách server đặt slug.
 */
export function luaChonThuMuc(ds: readonly Folder[], boNhanh?: string): Array<{ id: string; ten: string; sau: number }> {
  const cay = dungCay(ds);
  const phang = traiCay(cay, new Set(ds.map((f) => f.id)));
  const bo = new Set<string>();
  if (boNhanh !== undefined) {
    const goc = phang.find((n) => n.id === boNhanh);
    const gom = (n: NutCay, sau: number): void => {
      if (sau > 512) return;
      bo.add(n.id);
      for (const c of n.con) gom(c, sau + 1);
    };
    if (goc) gom(goc, 0);
  }
  return phang.filter((n) => !bo.has(n.id)).map((n) => ({ id: n.id, ten: n.name, sau: n.sau }));
}
